import React, { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import styles from "./GlobalLoader.module.css";

interface GlobalLoaderContextValue {
  showLoading: (text?: string) => void;
  hideLoading: () => void;
}

const GlobalLoaderContext = createContext<GlobalLoaderContextValue | undefined>(undefined);

export function GlobalLoaderProvider({ children }: { children: ReactNode }) {
  const [visible, setVisible] = useState(false);
  const [text, setText] = useState("Processando...");
  const safetyTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const showLoading = useCallback((nextText?: string) => {
    if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
    setText(nextText || "Processando...");
    setVisible(true);
    safetyTimeoutRef.current = setTimeout(() => {
      setVisible(false);
      safetyTimeoutRef.current = null;
    }, 8000);
  }, []);

  const hideLoading = useCallback(() => {
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
    setVisible(false);
  }, []);

  const value = useMemo(
    () => ({ showLoading, hideLoading }),
    [showLoading, hideLoading]
  );

  return (
    <GlobalLoaderContext.Provider value={value}>
      {children}
      {createPortal(
        <div className={`${styles.overlay} ${visible ? styles.show : ""}`} role="status" aria-live="polite">
          <div className={styles.panel}>
            <div className={styles.spinner} />
            <div className={styles.text}>{text}</div>
          </div>
        </div>,
        document.body
      )}
    </GlobalLoaderContext.Provider>
  );
}

export function useGlobalLoader(): GlobalLoaderContextValue {
  const context = useContext(GlobalLoaderContext);
  if (!context) {
    throw new Error("useGlobalLoader deve ser usado dentro de GlobalLoaderProvider.");
  }
  return context;
}
